/**
 * Privacy Policy Settings (tenant self-service).
 * Edits the same Tenant.settings.privacyPolicy field super-admin can also set
 * on this tenant's behalf (saas-front's TenantPrivacyPolicy.tsx) — either
 * writer converges on the same data. This tab only renders at all when
 * modules.privacyPolicy is on for this tenant (see Settings.tsx's TAB_DEFS
 * module gate); the backend independently enforces the same flag on the
 * underlying /privacy-policy endpoint, so hiding this tab is a UX nicety, not
 * the only guard.
 */
import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  InputAdornment,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Save, ContentCopy, OpenInNew } from '@mui/icons-material';
import adminApi, { PrivacyPolicyFields } from '@/services/admin.api';

const emptyForm = (): PrivacyPolicyFields => ({
  mode: 'platform_hosted',
  customUrl: '',
  legalBusinessName: '',
  category: '',
  address: '',
  contactEmail: '',
  dpoName: '',
  dpoEmail: '',
});

const PrivacyPolicySettings: React.FC = () => {
  const [formData, setFormData] = useState<PrivacyPolicyFields>(emptyForm());
  const [publicUrl, setPublicUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [copied, setCopied] = useState(false);

  const fetchPolicy = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await adminApi.getPrivacyPolicy();
      const policy = data?.policy ?? {};
      setFormData({
        mode: policy.mode || 'platform_hosted',
        customUrl: policy.customUrl || '',
        legalBusinessName: policy.legalBusinessName || '',
        category: policy.category || '',
        address: policy.address || '',
        contactEmail: policy.contactEmail || '',
        dpoName: policy.dpoName || '',
        dpoEmail: policy.dpoEmail || '',
        version: policy.version,
        publishedAt: policy.publishedAt,
      });
      setPublicUrl(data?.publicUrl || '');
    } catch (err: any) {
      setError(
        err.response?.data?.error?.message || err.message || 'Failed to load privacy policy'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPolicy();
  }, []);

  const handleField =
    (field: keyof PrivacyPolicyFields) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      const body: Partial<PrivacyPolicyFields> = { mode: formData.mode };
      if (formData.mode === 'custom') {
        body.customUrl = (formData.customUrl || '').trim();
      } else {
        body.legalBusinessName = (formData.legalBusinessName || '').trim();
        body.category = (formData.category || '').trim();
        body.address = (formData.address || '').trim();
        body.contactEmail = (formData.contactEmail || '').trim();
        body.dpoName = (formData.dpoName || '').trim();
        body.dpoEmail = (formData.dpoEmail || '').trim();
      }
      const result = await adminApi.updatePrivacyPolicy(body);
      setSuccess(result?.message || 'Privacy policy updated.');
      setPublicUrl(result?.publicUrl || publicUrl);
      await fetchPolicy();
    } catch (err: any) {
      setError(
        err.response?.data?.error?.message || err.message || 'Failed to save privacy policy'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard API unavailable — copy button silently no-ops */
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" mb={0.5}>
          Privacy Policy
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          This is the page you hand to Meta for WhatsApp Business Verification. If you already have
          a privacy policy on your own site, point to it below — otherwise we'll host one for you,
          built from the details you fill in here.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        <TextField
          label="Public URL — paste this into Meta"
          value={publicUrl}
          fullWidth
          size="small"
          sx={{ mb: 3 }}
          InputProps={{
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip title={copied ? 'Copied!' : 'Copy'}>
                  <IconButton size="small" onClick={handleCopy}>
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Tooltip>
                {publicUrl && (
                  <IconButton
                    size="small"
                    component="a"
                    href={publicUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <OpenInNew fontSize="small" />
                  </IconButton>
                )}
              </InputAdornment>
            ),
          }}
        />

        <ToggleButtonGroup
          value={formData.mode}
          exclusive
          size="small"
          sx={{ mb: 3 }}
          onChange={(_, val) => val && setFormData((prev) => ({ ...prev, mode: val }))}
        >
          <ToggleButton value="platform_hosted">Host it for me</ToggleButton>
          <ToggleButton value="custom">I already have one</ToggleButton>
        </ToggleButtonGroup>

        {formData.mode === 'custom' ? (
          <TextField
            label="Your existing Privacy Policy URL"
            value={formData.customUrl}
            onChange={handleField('customUrl')}
            fullWidth
            size="small"
            placeholder="https://example.com/privacy"
            helperText="The public URL above will redirect here — safe to hand to Meta even before this is filled in."
          />
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Legal business name"
                value={formData.legalBusinessName}
                onChange={handleField('legalBusinessName')}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Category"
                value={formData.category}
                onChange={handleField('category')}
                fullWidth
                size="small"
                placeholder="e.g. Travel and transportation"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Business address"
                value={formData.address}
                onChange={handleField('address')}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Contact email"
                value={formData.contactEmail}
                onChange={handleField('contactEmail')}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Grievance / DPO name"
                value={formData.dpoName}
                onChange={handleField('dpoName')}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Grievance / DPO email"
                value={formData.dpoEmail}
                onChange={handleField('dpoEmail')}
                fullWidth
                size="small"
              />
            </Grid>
          </Grid>
        )}

        <Box display="flex" justifyContent="space-between" alignItems="center" mt={3}>
          <Typography variant="caption" color="text.secondary">
            {formData.publishedAt
              ? `Last updated ${new Date(formData.publishedAt).toLocaleString()} · v${formData.version ?? 0}`
              : 'Never published'}
          </Typography>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
            onClick={handleSave}
            disabled={saving}
          >
            Save
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default PrivacyPolicySettings;
